import { Button, Loader, Menu, rem } from '@mantine/core';
import {
  isCancelled,
  MANUAL_TEMPLATE_KEYS,
  MAIL_TEMPLATES,
  type ManualTemplateKey,
} from '@reditelji/shared';
import { useState } from 'react';
import { useMailSends } from '../api/mail';
import { useThread, type VolunteerRow } from '../api/volunteers';
import { SendMailDialog } from './SendMailDialog';
import { sendStatesFor, shortDate, type TemplateState } from './sendState';
import './mail.css';

/**
 * Everything this person has been sent, and everything they can be sent.
 *
 * One card rather than a button per mail, because the question a coordinator
 * actually arrives with is "what has this person had?" — and the answer is only
 * useful when all four are read together, with the obvestila they got below.
 */
export function SendCard({
  volunteer,
  shiftCount,
  cardStyle,
  labelStyle,
}: {
  volunteer: VolunteerRow;
  shiftCount: number;
  cardStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
}) {
  const sends = useMailSends();
  const thread = useThread(volunteer.id);
  const [open, setOpen] = useState<ManualTemplateKey | null>(null);
  const states = sendStatesFor(volunteer, sends.data ?? [], shiftCount);
  // Obvestila are written from Sporočila, never from here: the card only says
  // which of them this person got. The thread holds their copies.
  const notices = (thread.data ?? [])
    .filter((m) => m.templateKey === 'obvestilo')
    .sort((a, b) => b.at.localeCompare(a.at));

  return (
    <div style={{ ...cardStyle, padding: 14 }}>
      <div style={{ ...labelStyle, marginBottom: 8, display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span>POŠILJANJE</span>
        {/* Every row below says why on its own; the head says it once for all
            of them (SPEC §1.1K). */}
        {isCancelled(volunteer) ? (
          <span style={{ fontSize: rem(12), fontWeight: 500, letterSpacing: 0, color: 'oklch(0.5 0.13 25)' }}>
            zaprto — oseba ne pride
          </span>
        ) : null}
      </div>
      {/* Until the send states are in, every row would read "še ni bilo
          poslano" with a live button — and a click inside that window re-sends
          a mail the person already has, rotating a link they are holding. */}
      {sends.isPending ? (
        <div className="ml-send-loading">
          <Loader size="xs" />
        </div>
      ) : (
      <div className="ml-send">
        {MANUAL_TEMPLATE_KEYS.map((key) => (
          <Row key={key} state={states[key]} onSend={() => setOpen(key)} />
        ))}
      </div>
      )}

      {notices.length > 0 ? (
        <div className="ml-send-notices">
          <div className="ml-send-name">Obvestila</div>
          {notices.map((m) => (
            <div key={m.id} className="ml-send-state">
              {m.subject || '— brez zadeve —'} · {shortDate(m.at)}
            </div>
          ))}
        </div>
      ) : null}

      {open ? (
        <SendMailDialog
          volunteerId={volunteer.id}
          volunteerName={volunteer.name}
          templateKey={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

function Row({ state, onSend }: { state: TemplateState; onSend: () => void }) {
  const def = MAIL_TEMPLATES[state.key];
  const sent = state.sentAt !== null;
  const blocked = state.blocked !== null;

  return (
    <div className="ml-send-row">
      <div className="ml-send-text">
        <div className="ml-send-name">{def.label}</div>
        <div
          className={`ml-send-state${
            state.stale ? ' ml-send-state--stale' : blocked && !sent ? ' ml-send-state--blocked' : ''
          }`}
        >
          {stateLine(state)}
        </div>
      </div>

      {sent ? (
        // Once sent, the primary action steps back: re-sending is available,
        // but it takes a deliberate second gesture.
        state.stale ? (
          <Button size="compact-xs" disabled={blocked} onClick={onSend}>
            Pošlji znova
          </Button>
        ) : (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button size="compact-xs" variant="subtle" aria-label={`Možnosti — ${def.label}`}>
                ⋮
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item disabled={blocked} onClick={onSend}>
                Pošlji znova
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )
      ) : (
        <Button
          size="compact-xs"
          variant="default"
          disabled={blocked}
          title={state.blocked ?? undefined}
          onClick={onSend}
        >
          Pošlji
        </Button>
      )}
    </div>
  );
}

function stateLine(state: TemplateState): string {
  if (state.sentAt === null) {
    return state.blocked ? state.blocked : 'še ni bilo poslano';
  }
  const when = shortDate(state.sentAt);
  const who = state.sentBy ? ` · ${state.sentBy}` : '';
  if (state.stale) return `poslano ${when} — razpored se je od takrat spremenil`;
  return `poslano ${when}${who}`;
}
